<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Print Button and Selection Actions -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>{{ $t("emergency.Patient Emergency Invoice") }}</h2>
              <div class="d-flex gap-2">
                <b-button
                  v-if="!allSelected"
                  @click="selectAll"
                  variant="outline-primary"
                  size="sm"
                >
                  <feather-icon icon="CheckSquareIcon" size="14" class="mr-1" />
                  {{ $t("common.select-all") }}
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  @click="showBulkUpdateModal"
                  variant="warning"
                  size="sm"
                >
                  <feather-icon icon="EditIcon" size="14" class="mr-1" />
                  {{ $t("common.bulk-update-status") }} ({{ selectedInvoices.length }})
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  @click="clearSelection"
                  variant="outline-secondary"
                  size="sm"
                >
                  {{ $t("common.clear-selection") }}
                </b-button>
              </div>
            </div>

            <!-- Data Table -->
            <b-table
              :items="this.invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
              v-if="!loading"
            >
              <!-- Checkbox for selection -->
              <template #cell(select)="data">
                <b-form-checkbox
                  v-model="selectedInvoices"
                  :value="data.item"
                  @change="onSelectionChange"
                />
              </template>

              <template #cell(invoice_no)="data">
                <span class="font-weight-bold text-primary">
                  #{{ data.item.invoice_no }}
                </span>
              </template>

              <template #cell(service_items)="data">
                <div v-if="getNestedProperty(data.item, 'invoice_items.length', 0) > 0" class="">
                  <!-- Show first item only -->
                  <div 
                    v-for="(item, index) in data.item.invoice_items.slice(0, 1)" 
                    :key="index"
                    class="test-item-row"
                  >
                    <div class="d-flex justify-content-between align-items-center py-1">
                      <div class="test-item-name text-nowrap ">
                        <b-badge variant="primary" class="mr-1" style="font-size: 0.85rem;">
                          {{ index + 1 }}
                        </b-badge>
                        <span class="font-weight-bold text-nowrap text-dark">
                          {{ getNestedProperty(item, 'name', 'N/A') }}
                        </span>
                      </div>
                      <div class="test-item-amount mx-2">
                        <span class="text-success text-nowrap font-weight-bolder" style="font-size: 0.95rem;">
                          {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Show more button if more than 1 item -->
                  <div v-if="data.item.invoice_items.length > 1" class="">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="text-nowrap"
                      block
                      @click="showAllServiceItems(data.item)"
                    >
                      <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                      {{ $t('common.show') }} {{ data.item.invoice_items.length - 1 }} {{ $t('common.more-items') }}
                    </b-button>
                  </div>
                </div>
                <span v-else class="text-muted">{{ $t('common.no-items') }}</span>
              </template>

              <template #cell(discount)="data">
                <span class="text-nowrap font-weight-bold text-warning">
                  {{ formatPrice(data.item.discount || 0) }}
                </span>
              </template>

              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bolder text-success">
                  {{ formatPrice(data.item.amount) }}
                </span>
              </template>

              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="emergencyInvoice"
                    @onEmit="$emit('onChangeStatus')"
                    endpoint="invoices"
                    :data="data.item"
                  />
                </div>
              </template>

              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved' "
                    @click="showEmergencyInvoice(data.item)"
                    variant="outline-primary"
                    size="sm"
                    class="mx-1"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-button
                    v-if="$store.state.app.permissions.includes('apply-invoice-discount') && data.item.status !== 'printed' && data.item.status !== 'approved'"
                    @click="showDiscountModal(data.item)"
                    variant="outline-warning"
                    size="sm"
                    class="mx-1"
                  >
                    <feather-icon icon="PercentIcon" size="14" class="mr-1" />
                    {{ $t("common.apply-discount") }}
                  </b-button>
                </div>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span class="text-muted"
                    >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  @click="getInvoices()"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon icon="ChevronLeftIcon" size="18" />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Modal to show all service items -->
      <b-modal
        id="modal-all-service-items"
        centered
        size="md"
        :title="$t('common.all-items') + ' - ' + $t('common.invoice-number') + ' #' + selectedInvoiceNo"
        hide-footer
      >
        <b-card-text>
          <div v-if="selectedInvoiceItems.length > 0">
            <div 
              v-for="(item, index) in selectedInvoiceItems" 
              :key="index"
              class="test-item-modal-row"
              :class="{ 'border-bottom': index < selectedInvoiceItems.length - 1 }"
            >
              <div class="d-flex justify-content-between align-items-center py-2">
                <div class="d-flex align-items-center flex-1">
                  <b-badge variant="primary" class="mr-2" style="font-size: 0.9rem;">
                    {{ index + 1 }}
                  </b-badge>
                  <span class="font-weight-bold text-dark">
                    {{ getNestedProperty(item, 'name', 'N/A') }}
                  </span>
                </div>
                <div class="ml-3">
                  <span class="text-success font-weight-bolder" style="font-size: 1rem;">
                    {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Total -->
            <div class="mt-3 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-weight-bold text-dark" style="font-size: 1.1rem;">
                  {{ $t('common.total') }}:
                </span>
                <span class="text-success font-weight-bolder" style="font-size: 1.2rem;">
                  {{ formatPrice(calculateModalTotal()) }}
                </span>
              </div>
            </div>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Bulk Update Status Modal -->
      <b-modal
        :no-close-on-backdrop="true"
        id="modal-bulk-update-status"
        :title="$t('common.bulk-update-status')"
        ok-only
        :ok-title="$t('common.update')"
        @ok="bulkUpdateStatus"
      >
        <b-card-text>
          <h5>{{ $t('common.bulk-update-status') }}</h5>
          <p>{{ $t('common.selected-invoices-count', { count: selectedInvoices.length }) }}</p>
          
          <b-form-group :label="$t('common.status')" label-for="bulk-status">
            <b-form-select
              id="bulk-status"
              v-model="bulkUpdateForm.status"
              :options="statusOptions"
              :placeholder="$t('common.select-status')"
            />
          </b-form-group>
          
          <div class="mt-3">
            <h6>{{ $t('common.selected-invoices') }}:</h6>
            <ul class="list-unstyled">
              <li v-for="invoice in selectedInvoices" :key="invoice.id" class="mb-1">
                <small class="text-muted">
                  #{{ getNestedProperty(invoice, 'invoice_no') }} - 
                  {{ formatPrice(getNestedProperty(invoice, 'amount')) }}
                </small>
              </li>
            </ul>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group 
            :label="$t('common.invoice-number')" 
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group 
            :label="$t('common.current-amount')" 
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatPrice(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group 
            :label="$t('common.discount-amount')" 
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert v-if="discountForm.discount > 0" variant="info" show class="mt-2">
            <div class="d-flex justify-content-between">
              <span>{{ $t('common.amount-after-discount') }}:</span>
              <strong>{{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>

      <!-- Print Confirmation Modal -->
      <b-modal
        id="modal-confirm-print-emergency"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- Hidden print button -->
      <b-button
        ref="EmergencyInvoicePrint"
        v-print="EmergencyInvoice"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("reception.Print Invoice") }}
      </b-button>

      <EmergencyInvoice :patientData="patientData" :data="invoice" />
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BTable,
  BBadge,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import { getNestedProperty } from '@/utils/nestedProperty.js';
const AutoComplete = () => import("@/components/common/AutoComplete.vue");
const EmergencyInvoice = () =>
  import("@/components/printables/EmergencyInvoice.vue");
const ChangeInvoiceStatusDropDown = () =>
  import("@/components/invoices/ChangeInvoiceStatusDropDown.vue");

import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";

export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    vSelect,
    ToastificationContent,
    VuePhoneNumberInput,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    BAlert,
    ChangeInvoiceStatusDropDown,
    EmergencyInvoice,
  },
  data() {
    return {
      tableColumns: [
        {
          key: "select",
          label: "",
          thStyle: { width: "50px" },
        },
        {
          key: "invoice_no",
          sortable: true,
          label: this.$t("hospital.invoice-no"),
        },
        {
          key: "service_items",
          label: this.$t("reception.service"),
          sortable: false,
        },
        {
          key: "discount",
          label: this.$t("common.discount"),
          sortable: true,
        },
        {
          key: "amount",
          label: this.$t("employee.amount"),
          sortable: true,
        },
        {
          key: "created_at",
          sortable: true,
          label: this.$t("common.created-at"),
        },
        {
          key: "status",
          sortable: true,
          label: "Status",
        },
        { key: "Actions", label: this.$t("common.actions") },
      ],
      invoiceLatestStatus: "",
      InvoiceId: "",
      invoice: "",
      currentInvoice: null,
      selectedInvoices: [],
      selectedInvoiceItems: [],
      selectedInvoiceNo: '',
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0
      },
      discountError: '',
      bulkUpdateForm: {
        status: null,
      },
      statusOptions: [
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      EmergencyInvoice: {
        id: "EmergencyInvoice",
      },
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: "",
      sortBy: "id",
      isSortDirDesc: true,
      page: 1,
      dataCount: 0,
      loading: false,
    };
  },
  props: ["invoices", "patientData"],

  computed: {
    allSelectedInvoicesApproved() {
      return this.selectedInvoices.length > 0 && 
             this.selectedInvoices.every(invoice => invoice.status === 'approved');
    },

    allSelected: {
      get() {
        const invoicesData = getNestedProperty(this.invoices, 'data', []);
        return invoicesData.length > 0 && this.selectedInvoices.length === invoicesData.length;
      },
      set(value) {
        // Handled by toggleSelectAll
      }
    }
  },

  mounted() {},
  activated() {
    // Refresh data when component becomes active
    this.$emit('onChangeStatus');
  },
  methods: {
    getNestedProperty,

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    // Show all service items in modal
    showAllServiceItems(invoice) {
      this.selectedInvoiceItems = getNestedProperty(invoice, 'invoice_items', []);
      this.selectedInvoiceNo = invoice.invoice_no || '';
      this.$bvModal.show('modal-all-service-items');
    },
    
    // Calculate total for modal
    calculateModalTotal() {
      return this.selectedInvoiceItems.reduce((sum, item) => {
        const amount = getNestedProperty(item, 'net_amount', 0);
        return sum + (parseFloat(amount) || 0);
      }, 0);
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0
      };
      this.discountError = '';
      this.$bvModal.show('modal-apply-discount');
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0;
      const discount = parseFloat(this.discountForm.discount) || 0;
      return Math.max(0, amount - discount);
    },

    // Apply discount to invoice
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault();
      
      // Validate discount
      const discount = parseFloat(this.discountForm.discount);
      const amount = parseFloat(this.discountForm.amount);
      
      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount');
        return;
      }
      
      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount');
        return;
      }

      try {
        this.loading = true;
        
        const payload = {
          discount: discount
        };

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload);

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("common.discount-applied-successfully"),
          },
        });

        this.$bvModal.hide('modal-apply-discount');
        
        // Refresh data
        this.$emit("onChangeStatus");
        
      } catch (error) {
        console.error("Error applying discount:", error);
        
        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data;
          
          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0];
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.error"),
                variant: "danger",
                text: errors.discount[0],
              },
            });
          } else {
            // Generic validation error
            const firstError = Object.values(errors)[0];
            const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            this.discountError = errorMessage;
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.error"),
                variant: "danger",
                text: errorMessage,
              },
            });
          }
        } else {
          // Generic error
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("common.discount-apply-error"),
            },
          });
        }
      } finally {
        this.loading = false;
      }
    },

    showEmergencyInvoice(item) {
      this.invoice = item;
      this.currentInvoice = item;
      this.$bvModal.show('modal-confirm-print-emergency');
    },

    async printInvoice() {
      try {
        // Trigger print
        this.$refs.EmergencyInvoicePrint.click();
        
        // Update status to printed
        await this.$http.put(`/invoices/${this.currentInvoice.id}/changeStatus`, {
          status: 'printed'
        });
        
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("common.invoice-printed-successfully"),
          },
        });
        
        // Refresh data
        this.$emit("onChangeStatus");
        
      } catch (error) {
        console.error("Error updating invoice status:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-updating-status"),
          },
        });
      }
    },

    clearSelection() {
      this.selectedInvoices = [];
    },

    selectAll() {
      const invoicesData = getNestedProperty(this.invoices, 'data', []);
      this.selectedInvoices = [...invoicesData];
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectAll();
      } else {
        this.clearSelection();
      }
    },

    showBulkUpdateModal() {
      if (this.selectedInvoices.length === 0) {
        this.$toast.error(this.$t("common.select-invoices-first"));
        return;
      }
      this.bulkUpdateForm.status = null;
      this.$bvModal.show("modal-bulk-update-status");
    },

    async bulkUpdateStatus() {
      if (!this.bulkUpdateForm.status) {
        this.$toast.error(this.$t("common.select-status-first"));
        return;
      }

      try {
        this.loading = true;
        
        // Prepare the payload
        const payload = {
          invoices: this.selectedInvoices.map(invoice => ({
            id: invoice.id,
            status: this.bulkUpdateForm.status
          }))
        };

        console.log("Bulk update payload:", payload);

        // Call the bulk update API
        await this.$http.post("/invoices/bulkUpdateStatus", payload);

        this.$toast.success(this.$t("common.bulk-update-success"));
        
        // Clear selection and refresh data
        this.clearSelection();
        this.$bvModal.hide("modal-bulk-update-status");
        
        // Emit event to refresh parent data
        this.$emit("onChangeStatus");
        
      } catch (error) {
        console.error("Error bulk updating invoices:", error);
        this.$toast.error(this.$t("common.bulk-update-error"));
      } finally {
        this.loading = false;
      }
    },

    onSelectionChange() {
      // Handle selection change if needed
    },
  },
};
</script>

<style scoped>
.hidden {
  display: none !important;
}

.gap-2 {
  gap: 0.5rem;
}

/* Service Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}
</style>
